"use client";

import { useId } from "react";

import { ActivityTerminal } from "@product/components/app/runs/activity-terminal";
import {
  RunTraceFooter,
  RunTraceOpenFrame,
  RunTracePill,
} from "@product/components/app/runs/run-trace-pill";
import {
  TaskEventTranscriptList,
  type EventPluginBadgeInput,
} from "@product/components/app/runs/task-event-transcript";
import { PluginBadgeView } from "@product/components/app/plugins/plugin-badge-view";
import type { RenderedRunEventBlock } from "@product/lib/task-run-ui";
import {
  SheetCloseContent,
  SheetHeaderView,
  sheetHeaderCloseClassName,
  sheetTitleClassName,
  sheetVariants,
} from "@product/components/ui/sheet";
import { cn } from "@product/lib/utils";

import { RUN, RUN_BLOCKS, RUN_PLUGINS } from "./fixture-run";
import { ShotClosedTrace } from "./window";

/** A step's plugin badge, resolved by slug from the run's plugins as
 *  findRuntimePluginForToolBadge resolves it from the live catalogue. */
export function shotPluginBadge(input: EventPluginBadgeInput) {
  const plugin = input.slug ? RUN_PLUGINS.find((entry) => entry.slug === input.slug) : undefined;
  return (
    <PluginBadgeView
      plugin={plugin}
      label={input.label}
      kind={input.kind}
      variant={input.variant}
      className={input.className}
    />
  );
}

/** The terminal a run trace opens into, as TaskRunActivity renders it. */
function ShotRunTerminal({
  blocks = RUN_BLOCKS,
  working = false,
  status = RUN.status,
  countsLabel = `${RUN.updateCountLabel} · ${RUN.durationLabel}`,
  closeLabel,
  onClose,
}: {
  blocks?: RenderedRunEventBlock[];
  working?: boolean;
  status?: string;
  countsLabel?: string;
  closeLabel: string;
  onClose?: () => void;
}) {
  const last = blocks[blocks.length - 1];
  return (
    <ActivityTerminal
      bodyClassName="max-h-[96px]"
      isWorking={working}
      scrollKey={`${blocks.length}:${last?.id ?? ""}`}
      footer={
        <RunTraceFooter status={status} countsLabel={countsLabel} closeLabel={closeLabel} onClose={onClose} />
      }
    >
      <TaskEventTranscriptList
        blocks={blocks}
        isRunning={working}
        className="gap-1.5"
        pluginBadge={shotPluginBadge}
      />
    </ActivityTerminal>
  );
}

/**
 * The run trace above the composer, closed or open. Closed, it is the pill
 * (one for phones, one for desktop, as TaskRunActivity renders both). Open,
 * the desktop pill becomes the trace's terminal in place; on a phone the
 * product opens the terminal in a bottom sheet, which the shot draws inside
 * its window.
 */
export function ShotRunTrace({
  open,
  onOpenChange,
  label = RUN.label,
  updateCountLabel = RUN.updateCountLabel,
  durationLabel = RUN.durationLabel,
  blocks,
  working = false,
  status,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  label?: string;
  updateCountLabel?: string;
  durationLabel?: string | null;
  blocks?: RenderedRunEventBlock[];
  working?: boolean;
  status?: string;
}) {
  const closeLabel = `Close ${label.toLowerCase()}`;
  const counts = `${updateCountLabel}${durationLabel ? ` · ${durationLabel}` : ""}`;
  const terminal = (
    <ShotRunTerminal
      blocks={blocks}
      working={working}
      status={status}
      countsLabel={counts}
      closeLabel={closeLabel}
      onClose={() => onOpenChange(false)}
    />
  );
  if (!open) {
    return (
      <ShotClosedTrace
        onOpen={() => onOpenChange(true)}
        label={label}
        updateCountLabel={updateCountLabel}
        durationLabel={durationLabel}
        running={working}
      />
    );
  }
  return (
    <RunTraceOpenFrame
      className="w-auto min-w-0 max-w-none flex-auto"
      pill={
        <RunTracePill
          label={label}
          updateCountLabel={updateCountLabel}
          durationLabel={durationLabel}
          running={working}
          onClick={() => onOpenChange(true)}
        />
      }
      terminal={terminal}
    />
  );
}

/**
 * The phone's run trace sheet (TaskRunActivity's MobileRunTraceSheet),
 * drawn inside the shot's window. Render it beside the transcript while the
 * trace is open; it only shows below 1024px, as in the product.
 */
export function ShotMobileRunTraceSheet({
  open,
  onOpenChange,
  label = RUN.label,
  blocks,
  working = false,
  status,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  label?: string;
  blocks?: RenderedRunEventBlock[];
  working?: boolean;
  status?: string;
}) {
  const titleId = useId();
  if (!open) return null;
  return (
    <div className="lg:hidden">
      <div aria-hidden="true" className="absolute inset-0 z-(--z-backdrop) bg-black/10 dark:bg-black/40" onClick={() => onOpenChange(false)} />
      <div
        role="dialog"
        aria-labelledby={titleId}
        data-slot="sheet-content"
        data-side="bottom"
        data-open=""
        className={cn(
          sheetVariants({ side: "bottom", size: "sm" }),
          "flex flex-col overflow-hidden",
          "max-h-[75dvh] rounded-t-xl lg:hidden",
          "absolute motion-reduce:animate-none!",
        )}
      >
        <SheetHeaderView
          close={
            <button
              type="button"
              data-slot="sheet-header-close"
              className={sheetHeaderCloseClassName}
              onClick={() => onOpenChange(false)}
            >
              <SheetCloseContent />
            </button>
          }
        >
          <h2 id={titleId} data-slot="sheet-title" className={sheetTitleClassName}>
            {label}
          </h2>
        </SheetHeaderView>
        <div className="min-h-0 flex-1 overflow-y-auto p-3">
          <ShotRunTerminal
            blocks={blocks}
            working={working}
            status={status}
            closeLabel={`Close ${label.toLowerCase()}`}
            onClose={() => onOpenChange(false)}
          />
        </div>
      </div>
    </div>
  );
}
